/* ============================================================================
 * GESKE - Gift with purchase surfaces outside the cart drawer
 * ----------------------------------------------------------------------------
 * geske-cart.js works out the gift-with-purchase state from /cart.js (gift
 * lines carry a _gwp property) and broadcasts it as a `geske:cart` event with
 * detail.gwp = { active, unlocked, remaining, progress, values: {amount, value,
 * threshold, count}, ... }. This file listens and swaps two surfaces:
 *
 *   1. The site-wide newsletter offer bar ([data-nlo-bar]): the "save 20%"
 *      pitch + email capture give way to "Add {amount} more to receive your
 *      free gift worth {value}" with a small meter.
 *   2. The PDP buy-box offer card ([data-nlo-pdp]): the "New here? Take 20%
 *      off" capture gives way to the same message, with a note when adding
 *      the current device alone reaches the threshold.
 *
 * Both revert automatically when the gift lines leave the cart.
 * ========================================================================== */
(function () {
  'use strict';

  // Fallback copy when the section/block setting is empty (the Liquid side
  // cannot carry {placeholder} defaults: Shopify's parser trips on braces).
  var DEFAULTS = {
    barPending: 'Add {amount} more to receive your free gift worth {value}',
    barComplete: 'Your free gift worth {value} is unlocked and ships free with your order',
    pdpHeading: 'Add {amount} more to receive your free gift worth {value}',
    pdpText: 'Your skincare bundle is waiting in your bag. It ships free once your devices reach {threshold}.',
    pdpThisDevice: 'Adding this device unlocks it.',
    pdpUnlockedHeading: 'Your free gift worth {value} is unlocked',
    pdpUnlockedText: 'Your skincare bundle ships free with this order.'
  };

  function attr(el, name, fallback) {
    var value = el.getAttribute(name);
    return value && value.trim() ? value : fallback;
  }

  function fill(template, values) {
    if (window.GeskeCart && window.GeskeCart.fillTemplate) {
      return window.GeskeCart.fillTemplate(template, values);
    }
    return String(template || '').replace(/\{(\w+)\}/g, function (match, key) {
      return values && Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  function openBag(event) {
    if (event) event.preventDefault();
    if (window.GeskeCart && window.GeskeCart.open) window.GeskeCart.open();
  }

  /* --------------------------- announcement bar --------------------------- */
  function updateBar(gwp) {
    var bar = document.querySelector('[data-nlo-bar]');
    if (!bar) return;
    var block = bar.querySelector('[data-gwp-bar]');
    if (!block) return;

    var active = !!(gwp && gwp.active);
    bar.classList.toggle('is-gwp', active);
    bar.classList.toggle('is-gwp-unlocked', active && !!gwp.unlocked);
    block.hidden = !active;
    if (!active) { resync(); return; }

    var text = block.querySelector('[data-gwp-bar-text]');
    var fillEl = block.querySelector('[data-gwp-bar-fill]');
    var template = gwp.unlocked
      ? attr(block, 'data-gwp-complete', DEFAULTS.barComplete)
      : attr(block, 'data-gwp-pending', DEFAULTS.barPending);
    if (text) text.textContent = fill(template, gwp.values);
    if (fillEl) fillEl.style.width = (gwp.unlocked ? 100 : gwp.progress) + '%';
    resync();
  }

  // The bar's height feeds the fixed-header offset (geske-scripts.js measures it
  // on scroll/resize), so nudge a re-measure after the content swap.
  var resyncTimer = null;
  function resync() {
    if (resyncTimer) return;
    resyncTimer = window.setTimeout(function () {
      resyncTimer = null;
      try { window.dispatchEvent(new Event('resize')); } catch (error) { /* no-op */ }
    }, 30);
  }

  /* -------------------------------- PDP card ------------------------------ */
  function updatePdp(gwp) {
    var wraps = document.querySelectorAll('[data-nlo-pdp]');
    if (!wraps.length) return;

    Array.prototype.forEach.call(wraps, function (wrap) {
      var card = wrap.querySelector('[data-gwp-pdp]');
      var defaultCard = wrap.querySelector('[data-nlo-pdp-default]');
      if (!card) return;

      var active = !!(gwp && gwp.active);
      wrap.classList.toggle('is-gwp', active);
      card.hidden = !active;
      if (defaultCard) defaultCard.hidden = active;
      if (!active) return;

      card.classList.toggle('is-unlocked', !!gwp.unlocked);
      var heading = card.querySelector('[data-gwp-pdp-heading]');
      var text = card.querySelector('[data-gwp-pdp-text]');
      var fillEl = card.querySelector('[data-gwp-pdp-fill]');

      if (gwp.unlocked) {
        if (heading) heading.textContent = fill(attr(card, 'data-gwp-unlocked-heading', DEFAULTS.pdpUnlockedHeading), gwp.values);
        if (text) text.textContent = fill(attr(card, 'data-gwp-unlocked-text', DEFAULTS.pdpUnlockedText), gwp.values);
        if (fillEl) fillEl.style.width = '100%';
        return;
      }

      if (heading) heading.textContent = fill(attr(card, 'data-gwp-heading', DEFAULTS.pdpHeading), gwp.values);
      var body = fill(attr(card, 'data-gwp-text', DEFAULTS.pdpText), gwp.values);

      // If this device on its own closes the gap, say so (only when the product
      // counts towards the threshold, i.e. it is in the eligible collection).
      var productId = parseInt(card.getAttribute('data-gwp-product-id'), 10);
      var price = parseInt(card.getAttribute('data-gwp-product-price'), 10);
      var eligibleIds = eligibleProductIds();
      var counts = !eligibleIds.length || eligibleIds.indexOf(productId) !== -1;
      var note = attr(card, 'data-gwp-this-device', DEFAULTS.pdpThisDevice);
      if (counts && note && price > 0 && price >= gwp.remaining) {
        body = body ? body + ' ' + note : note;
      }
      if (text) text.textContent = body;
      if (fillEl) fillEl.style.width = gwp.progress + '%';
    });
  }

  function eligibleProductIds() {
    var drawer = document.querySelector('[data-cart-drawer]');
    var raw = drawer ? drawer.getAttribute('data-gwp-eligible-ids') || '' : '';
    return raw.split(',').map(function (id) { return parseInt(id, 10); }).filter(function (id) { return !isNaN(id); });
  }

  /* ------------------------ rule-driven gifts (engine) --------------------- */
  // When the legacy landing-page bundle is not in the bag, mirror the app-managed
  // rules instead: the nearest unmet spend threshold (pending) or the top
  // unlocked gift. Shaped like the legacy summary so updateBar/updatePdp reuse.
  function fromEngine(ev) {
    if (!ev || !ev.hasConfig) return null;
    var rule = ev.next || (ev.unlockedTop && ev.unlockedTop.present.length ? ev.unlockedTop : null);
    if (!rule) return null;
    var unlocked = !ev.next;
    return {
      active: true,
      unlocked: unlocked,
      remaining: rule.remaining,
      progress: unlocked ? 100 : rule.progress,
      values: rule.values,
      messages: rule.messages,
      engineRule: rule
    };
  }

  /* --------------------------------- wiring ------------------------------- */
  function apply(gwp, ev) {
    var summary = gwp && gwp.active ? gwp : fromEngine(ev || (gwp && gwp.engine));
    if (summary && summary.engineRule) {
      // Rule messages come from the app, not the section settings.
      var text = summary.unlocked ? summary.messages.unlocked : summary.messages.pending;
      var bar = document.querySelector('[data-nlo-bar] [data-gwp-bar]');
      if (bar) {
        bar.setAttribute('data-gwp-pending', summary.messages.pending);
        bar.setAttribute('data-gwp-complete', summary.messages.unlocked);
      }
      Array.prototype.forEach.call(document.querySelectorAll('[data-nlo-pdp] [data-gwp-pdp]'), function (card) {
        card.setAttribute('data-gwp-heading', text);
        card.setAttribute('data-gwp-unlocked-heading', summary.messages.unlocked);
        card.setAttribute('data-gwp-text', '');
        card.setAttribute('data-gwp-unlocked-text', '');
        card.setAttribute('data-gwp-this-device', '');
      });
    }
    updateBar(summary);
    updatePdp(summary);
  }

  document.addEventListener('geske:cart', function (event) {
    apply(event.detail && event.detail.gwp, event.detail && event.detail.engine);
  });

  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-gwp-bar-open], [data-gwp-pdp-open]')) openBag(event);
  });

  function init() {
    if (window.GeskeCart && window.GeskeCart.gwp) {
      var current = window.GeskeCart.gwp();
      if (current) apply(current, window.GeskeCart.engine ? window.GeskeCart.engine() : null);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
